import { getComposeRuntimeControls, isGroupRuntimeControlEnabled, getReadRuntimeControls, isReadGroupRuntimeControlEnabled, } from "./getRuntimeControls";
import { modifyIdsForCompose } from "./modifyIdsForCompose";
import { modifyIdsForRead } from "./modifyIdsForRead";
import { changeGroupLayout, dropLabel, moveToOverflowWell } from "owa-command-ribbon";
import { getComposeRibbonId } from "owa-compose-ribbon-utils/lib/utils/getComposeRibbonId";
import { owaComputedFn } from "owa-computed-fn";
import { type LabelPreferenceId } from "owa-mail-ribbon-store-shared-types/lib/util/labelPreferencesIds";
import { getMailRibbonConfigStore } from "owa-mail-ribbon-store/lib/store/store";
import { getReadRibbonId } from "owa-mail-ribbon-utils/lib/getReadRibbonId";
import { getMLRHomeTabGroupLayouts } from "../components/MLR/mlr-home-tab/getMLRHomeTabGroupLayouts";
import type { ReadOnlyRibbonControlDefProps, RibbonControlDefinitionProps, } from "owa-mail-ribbon-utils";
import type { RibbonScalingStep } from "owa-acui";
import type { ClientItemId } from "owa-client-ids";
import type { MailRibbonGroup } from "owa-mail-ribbon-store-shared-types";
import type { RibbonControlId } from "owa-ribbon-ids";
import { type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { RibbonRuntimeControlsGroup } from "./getRuntimeControls";

/**
 * MLR scaling can be pretty complex. At this time, we haven't created a general means to
 * represent it.  For now, we'll create the scaling steps "by hand"
 */
export const getMLRScalingSteps: (tab: MailRibbonTabId, props: RibbonControlDefinitionProps) => RibbonScalingStep[] = owaComputedFn(function getMLRScalingSteps(tab: MailRibbonTabId, props: RibbonControlDefinitionProps): RibbonScalingStep[] {
    const scalingSteps: RibbonScalingStep[] = new Array();
    
    switch (tab) {
        case 1: {
            // Tab_Home is only ever rendered with read-only props (compose tabs use
            // Tab_Format/Tab_Insert/etc. and never hit this branch).
            const readProps = props as ReadOnlyRibbonControlDefProps;
            // Walk the user's configured home tab groups right-to-left so the rightmost
            // groups scale down first. For each group, push one changeGroupLayout per
            // additional layout beyond the initial one (N layouts -> N-1 steps).
            const homeTabGroups = getMailRibbonConfigStore(readProps.mailboxInfo).multiline.homeTab
                .layout;
            for (let i = homeTabGroups.length - 1; i >= 0; i--) {
                const groupId = homeTabGroups[i].groupId;
                const layoutCount = getMLRHomeTabGroupLayouts(groupId, readProps)?.length ?? 0;
                for (let j = 0; j < layoutCount - 1; j++) {
                    scalingSteps.push(changeGroupLayout(groupId));
                }
            }
            
            /* ADO171622: `convertGroupToFlyout is currently causing issues and crashing when provoked.
                          Currently disabling these for the time being until the issue can be fixed. */
            // scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Tags));
            // scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Move));
            // scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Groups));
            // scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Find));
            break;
        }
        
        default:
            break;
    }
    
    return scalingSteps;
});

/**
 * Converts the saved ordering of RibbonIds to a series of scaling action functions
 * (drop label or move to overflow well).
 * @returns a series of RibbonScalingStep that represent the order in which scaling
 * should occur
 */
export const getScalingSteps: (storedRibbonConfig: MailRibbonGroup[], showLabelsPreference: LabelPreferenceId, controlsDontMoveToOverflow: RibbonControlId[], controlsFirstMoveToOverflow: RibbonControlId[], controlsDontDropLabels: RibbonControlId[], editorId: string | undefined, itemId: ClientItemId | undefined) => RibbonScalingStep[] = owaComputedFn(function getScalingSteps(storedRibbonConfig: MailRibbonGroup[], showLabelsPreference: LabelPreferenceId, controlsDontMoveToOverflow: RibbonControlId[], controlsFirstMoveToOverflow: RibbonControlId[], controlsDontDropLabels: RibbonControlId[], editorId: string | undefined, itemId: ClientItemId | undefined): RibbonScalingStep[] {
    const scalingSteps: RibbonScalingStep[] = new Array();
    
    /**
     * In the below for loop we are iterating from the end to the beginning of the scalingConfig
     * array as we want to drop the labels on the right-most group's buttons first. The right-most
     * group's buttons were those that were added last to the scalingConfig array last. This will
     * drop labels from the right-most group, and all the elements within the group as the same time.
     */
    if (showLabelsPreference === 1) {
        for (let groupIndex = storedRibbonConfig.length - 1; groupIndex >= 0; groupIndex--) {
            const perGroupRibbonIds: RibbonControlId[] = storedRibbonConfig[groupIndex].controlIds;
            
            //some controls we never want to drop labels for
            const controlsThatDropLabels: RibbonControlId[] = perGroupRibbonIds.filter(control => !controlsDontDropLabels.includes(control));
            // Drop labels for controls that are added at runtime.
            if (isGroupRuntimeControlEnabled(storedRibbonConfig[groupIndex].groupId)) {
                const runtimeControls: RibbonRuntimeControlsGroup = getComposeRuntimeControls();
                if (runtimeControls.shouldAddScalingSteps()) {
                    controlsThatDropLabels.push(...runtimeControls.getControlIds());
                }
            }
            
            if (isReadGroupRuntimeControlEnabled(storedRibbonConfig[groupIndex].groupId, false /* isMultiline */)) {
                const readRuntimeControls: RibbonRuntimeControlsGroup = getReadRuntimeControls();
                if (readRuntimeControls.shouldAddScalingSteps() &&
                    readRuntimeControls.getNumControls() > 0) {
                    controlsThatDropLabels.push(...readRuntimeControls.getControlIds());
                }
            }
            
            //drops labels for all controls within the group at groupIndex, simultaneously
            if (controlsThatDropLabels.length > 0) {
                if (itemId) {
                    scalingSteps.push(dropLabel(...modifyIdsForRead(controlsThatDropLabels, itemId)));
                }
                else {
                    scalingSteps.push(dropLabel(...modifyIdsForCompose(controlsThatDropLabels, editorId)));
                }
            }
        }
    }
    
    if (showLabelsPreference === 2) {
        const runtimeControls: RibbonRuntimeControlsGroup = getReadRuntimeControls();
        if (runtimeControls.shouldAddScalingSteps() && runtimeControls.getNumControls() > 0) {
            const runtimeIds = runtimeControls.getControlIds();
            if (itemId) {
                scalingSteps.push(dropLabel(...modifyIdsForRead(runtimeIds, itemId)));
            }
            else {
                scalingSteps.push(dropLabel(...modifyIdsForCompose(runtimeIds, editorId)));
            }
        }
    }
    
    const addToOverFlow = (controlId: RibbonControlId) => {
        let idToMove: RibbonControlId | string;
        if (!!editorId) {
            idToMove = getComposeRibbonId(controlId, editorId);
        }
        else if (itemId) {
            idToMove = getReadRibbonId(controlId, itemId);
        }
        else {
            idToMove = controlId;
        }
        scalingSteps.push(moveToOverflowWell(idToMove));
    };
    
    // controls that first move to the overflow by customization
    if (controlsFirstMoveToOverflow.length > 0) {
        const perGroupRibbonIds: RibbonControlId[] = controlsFirstMoveToOverflow;
        for (let i = 0; i < perGroupRibbonIds.length; i++) {
            if (!controlsDontMoveToOverflow.includes(perGroupRibbonIds[i])) {
                addToOverFlow(perGroupRibbonIds[i]);
            }
        }
    }
    // controls that move to the overflow from right to left by default
    for (let groupIndex = storedRibbonConfig.length - 1; groupIndex >= 0; groupIndex--) {
        // Move controls that are added at runtime to the overflow menu.
        
        if (isGroupRuntimeControlEnabled(storedRibbonConfig[groupIndex].groupId)) {
            const runtimeControls: RibbonRuntimeControlsGroup = getComposeRuntimeControls();
            if (runtimeControls.shouldAddScalingSteps()) {
                const controlIds = runtimeControls.getControlIds();
                for (let i = controlIds.length - 1; i >= 0; i--) {
                    addToOverFlow(controlIds[i]);
                }
            }
        }
        
        if (isReadGroupRuntimeControlEnabled(storedRibbonConfig[groupIndex].groupId, false /* isMultiline */)) {
            const readRuntimeControls: RibbonRuntimeControlsGroup = getReadRuntimeControls();
            if (readRuntimeControls.shouldAddScalingSteps() &&
                readRuntimeControls.getNumControls() > 0) {
                const controlIds = readRuntimeControls.getControlIds();
                for (let i = controlIds.length - 1; i >= 0; i--) {
                    addToOverFlow(controlIds[i]);
                }
            }
        }
        
        //iterates through every control from right to left and adds a move to overflow scaling step
        const perGroupRibbonIds: RibbonControlId[] = storedRibbonConfig[groupIndex].controlIds;
        for (let i = perGroupRibbonIds.length - 1; i >= 0; i--) {
            if (!controlsDontMoveToOverflow.includes(perGroupRibbonIds[i]) &&
                !controlsFirstMoveToOverflow.includes(perGroupRibbonIds[i])) {
                addToOverFlow(perGroupRibbonIds[i]);
            }
        }
    }
    
    return scalingSteps;
});
